/* eslint-disable react/prop-types */
import { FC, useId } from "react";
import { Loader2 } from "lucide-react";
import type { VideoEncodingProgressState } from "./useVideoEncodingProgress";

const formatElapsed = (seconds: number): string => {
	if (seconds < 60) return `${seconds}s`;
	const m = Math.floor(seconds / 60);
	const s = seconds % 60;
	return `${m}m ${s}s`;
};

const phaseLabel = (status?: string): string => {
	switch (status) {
		case "queued":
		case "created":
			return "Queued for encoding";
		case "running":
			return "Encoding video";
		case "finished":
			return "Almost ready";
		case "error":
			return "Encoding failed";
		case "canceled":
			return "Encoding canceled";
		default:
			return "Preparing video for playback";
	}
};

interface Props {
	state: VideoEncodingProgressState;
	/** Visual variant matching `VideoUploadProgress`. */
	variant?: "overlay" | "inline";
}

/**
 * Shared progress UI for the post-upload Bitmovin encoding phase.
 *
 * The previous "Preparing video for playback…" spinner gave no
 * feedback for 2–10 minutes. This component reads `useVideoEncodingProgress`
 * state (Bitmovin's live progress %) and renders a real bar plus
 * elapsed-time so authors know it's progressing rather than stuck.
 *
 * When Bitmovin progress isn't yet available (queued, or no encoding
 * id tracked for legacy videos), the bar runs as an indeterminate
 * shimmer alongside the elapsed time and phase label.
 *
 * Accessibility:
 * - The progressbar carries an explicit accessible name via
 *   `aria-labelledby`, so screen readers announce e.g. "Encoding
 *   video, 42 percent" instead of a bare percentage.
 * - `aria-live="polite"` is scoped to the label only — not the whole
 *   block — so polled updates re-announce a short string (~"Encoding
 *   video 42%") instead of the full label + bar + meta line every 4s.
 * - The Loader2 SVG is marked decorative; the percentage and label
 *   already convey state.
 * - Indeterminate state drops `aria-valuemin/max/now` so AT doesn't
 *   announce "0 of 100" while the bar is actually a shimmer.
 */
const VideoProcessingProgress: FC<Props> = ({ state, variant = "inline" }) => {
	const { progress, elapsedSeconds } = state;
	const isOverlay = variant === "overlay";
	const labelId = useId();

	const wrapperClass = isOverlay
		? "absolute inset-0 flex flex-col items-center justify-center bg-black/60 text-white text-sm font-medium gap-3 p-6"
		: "flex flex-col items-stretch gap-2 rounded-lg border border-border bg-subtle p-4 text-sm";
	const barTrackClass = isOverlay
		? "w-full max-w-md h-2 rounded-full bg-white/20 overflow-hidden"
		: "w-full h-2 rounded-full bg-border/40 overflow-hidden";
	const barFillClass = isOverlay
		? "h-full bg-white transition-[width] duration-200"
		: "h-full bg-primary transition-[width] duration-200";
	const indeterminateFillClass = isOverlay
		? "h-full w-1/3 bg-white/80 rounded-full animate-pulse"
		: "h-full w-1/3 bg-primary/80 rounded-full animate-pulse";
	const metaClass = isOverlay
		? "text-xs opacity-90 flex flex-wrap items-center justify-center gap-x-3 gap-y-1"
		: "text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1";

	const status = progress?.status;
	const rawPct = progress?.progress;
	// Numeric progress (even negative or >100) puts us in determinate
	// mode; we clamp visually below. `null`/missing falls back to the
	// indeterminate shimmer.
	const showDeterminate = typeof rawPct === "number";
	// Clamp ONCE — the visible bar, the label percentage, and the
	// aria-valuenow attribute all read from the same value so a wild
	// Bitmovin response (e.g. 175) renders consistently as 100.
	const clampedPct = showDeterminate
		? Math.min(100, Math.max(0, rawPct))
		: null;
	const label = phaseLabel(status);

	const indeterminateAriaProps = {} as Record<string, never>;
	const determinateAriaProps = {
		role: "progressbar" as const,
		"aria-valuemin": 0,
		"aria-valuemax": 100,
		"aria-valuenow": clampedPct ?? 0,
		"aria-labelledby": labelId
	};

	return (
		<div data-testid="video-processing-progress" className={wrapperClass}>
			<div className="flex items-center gap-2">
				<Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
				{/* Live region scoped to the label only — re-announces just
				    the short text on each Bitmovin poll. */}
				<span id={labelId} aria-live="polite">
					{showDeterminate ? `${label} ${clampedPct}%` : `${label}…`}
				</span>
			</div>
			<div
				className={barTrackClass}
				{...(showDeterminate ? determinateAriaProps : indeterminateAriaProps)}
			>
				{showDeterminate ? (
					<div
						className={barFillClass}
						style={{ width: `${clampedPct}%` }}
						data-testid="video-processing-progress-fill"
						aria-hidden="true"
					/>
				) : (
					<div className={indeterminateFillClass} aria-hidden="true" />
				)}
			</div>
			<div className={metaClass}>
				<span>Elapsed {formatElapsed(elapsedSeconds)}</span>
				{!showDeterminate && status === undefined && (
					<span>Typically 2–5 minutes</span>
				)}
			</div>
		</div>
	);
};

export default VideoProcessingProgress;
